import { Link } from "react-router-dom";
import type {
  BuyerSequenceBrokerDto,
  BuyerSequenceEditTarget,
  BuyerSequenceFieldRowDto,
  BuyerSequenceSelectionMode,
  BuyerSequenceZoneDto,
} from "@/api/buyerSequence";
import { Card } from "@/design-system/components/ui";
import { PROFILE_EDIT_PATH } from "./outreachCopy";

/** The card's section heading — shared by the three right-column cards. */
export const OUTREACH_CARD_HEADING =
  "m-0 font-display text-label font-semibold uppercase tracking-label text-gold-shadow";

/**
 * Where a value is fixed AT ITS SOURCE. A route for a page, or `call-hours` —
 * which is not a page at all but the Edit wording mode, where the call hours
 * a message quotes are set.
 */
function editDoor(
  target: BuyerSequenceEditTarget,
  buyerId: string,
): { to: string; label: string } | "call-hours" | null {
  switch (target) {
    // The buyer's phone and email are edited on the Overview's Contact card.
    case "buyer-contact":
      return { to: `/buyers/${buyerId}?tab=overview`, label: "Edit the buyer's contact" };
    case "profile":
      return { to: PROFILE_EDIT_PATH, label: "Edit your profile" };
    case "associated-listings":
      return { to: `/buyers/${buyerId}?tab=associated-listings`, label: "Open associated listings" };
    case "submissions":
      return { to: `/buyers/${buyerId}?tab=submissions`, label: "Open submissions" };
    case "call-hours":
      return "call-hours";
    case "none":
      return null;
  }
}

/**
 * "Pulled from this record" — every value the messages on screen merged, where
 * it came from, and where to fix it. The caller passes only the fields those
 * messages use (L3, `fieldsInUse`); with no message on screen there is nothing
 * to have pulled, and the card says so rather than claiming the message merges
 * nothing.
 *
 * <p>A value that fell back reads `Missing — using '…'` in the warning style,
 * because the message on the right is quoting the fallback and the specialist
 * should decide whether that is good enough to send before they copy it. The
 * door beside it goes to where the real value is edited, never to a form on
 * this tab: this tab renders from the record and writes nothing but the log.</p>
 */
export function PulledFromCard({
  buyerId,
  fields,
  messagesShown,
  broker,
  selectionMode,
  buyerZone,
  onEditCallHours,
}: {
  buyerId: string;
  fields: BuyerSequenceFieldRowDto[];
  messagesShown: boolean;
  broker: BuyerSequenceBrokerDto | null;
  selectionMode: BuyerSequenceSelectionMode;
  buyerZone: BuyerSequenceZoneDto;
  onEditCallHours: () => void;
}) {
  return (
    <Card data-testid="outreach-pulled-from">
      <h3 className={OUTREACH_CARD_HEADING}>Pulled from this record</h3>

      {fields.length === 0 ? (
        <p className="mb-0 mt-space-3 text-small text-gray-mid" data-testid="outreach-pulled-from-empty">
          {messagesShown
            ? "This message merges nothing from the record."
            : "Nothing to show until the messages can be written."}
        </p>
      ) : (
        <ul className="m-0 mt-space-3 grid list-none gap-space-3 p-0">
          {fields.map((row) => (
            <FieldRow
              key={row.field}
              row={row}
              buyerId={buyerId}
              onEditCallHours={onEditCallHours}
            />
          ))}
        </ul>
      )}

      <div className="mt-space-3 grid gap-space-1 border-t border-border-light pt-space-3">
        {/* G4 — the broker on the selected listing. Only meaningful once a
            listing IS selected; with "No listing" or nothing picked there is no
            listing to have a broker. */}
        {selectionMode === "listing" && (
          <p className="m-0 text-small text-onyx" data-testid="outreach-broker">
            {broker
              ? `Associated broker: ${broker.name}${broker.firm ? ` · ${broker.firm}` : ""}`
              : "No broker on this listing"}
          </p>
        )}
        {/* R-c — the zone the call-hours sentence was written in, and why. */}
        <p className="m-0 text-small text-onyx" data-testid="outreach-buyer-zone">
          Buyer&apos;s time zone: {buyerZone.label}{" "}
          <span className="text-gray-mid">({buyerZone.sourceDescription})</span>
        </p>
      </div>
    </Card>
  );
}

function FieldRow({
  row,
  buyerId,
  onEditCallHours,
}: {
  row: BuyerSequenceFieldRowDto;
  buyerId: string;
  onEditCallHours: () => void;
}) {
  const flagged = row.usedFallback || row.missing;
  const door = flagged ? editDoor(row.editTarget, buyerId) : null;

  return (
    <li className="grid min-w-0 gap-space-1" data-testid={`outreach-field-${row.field}`}>
      <span className="text-caption uppercase tracking-wide text-gray-mid">{row.label}</span>
      {row.usedFallback ? (
        <span className="text-small text-status-warning-ink" data-testid="outreach-field-fallback">
          {`Missing — using '${row.value}'`}
        </span>
      ) : row.missing ? (
        <span className="text-small text-status-warning-ink" data-testid="outreach-field-missing">
          Missing
        </span>
      ) : (
        <span className="break-words text-small text-onyx">{row.value}</span>
      )}
      <span className="text-caption text-gray-mid">{row.source}</span>
      {door === "call-hours" ? (
        <button
          type="button"
          onClick={onEditCallHours}
          className="justify-self-start text-caption text-gold-shadow hover:underline"
        >
          Set your call hours
        </button>
      ) : door ? (
        <Link to={door.to} className="justify-self-start text-caption text-gold-shadow hover:underline">
          {door.label}
        </Link>
      ) : null}
    </li>
  );
}
